import { useRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

import styles from "./custom-input-text.module.scss";

const CustomInputText = ({
	id,
	name,
	label,
	value,
	onChange,
	placeholder = "",
	required = false,
	disabled = false,
	error = "",
	helper = "",
	size = "md",
	col = 12,
	type = "text",
	autoFocus = false,
	autoComplete,
	addonBefore,
	addonAfter,
	className,
	style,
}) => {
	const [showPassword, setShowPassword] = useState(false);
	const stableIdRef = useRef(id || `${name || "input"}-${Math.random().toString(36).slice(2)}`);
	const inputId = stableIdRef.current;
	const colClass = col === 3 ? styles.col3 : col === 6 ? styles.col6 : styles.col12;
	const describedBy = error ? `${inputId}-error` : helper ? `${inputId}-helper` : undefined;

	const isPassword = type === "password";
	const inputType = isPassword && showPassword ? "text" : type;
	const hasAddonBefore = Boolean(addonBefore);
	const hasAddonAfter = Boolean(addonAfter);
	const hasAddons = hasAddonBefore || hasAddonAfter;

	const inputGroupClass = [
		styles.inputGroup,
		size === "sm" ? styles.inputGroupSm : size === "lg" ? styles.inputGroupLg : "",
		hasAddonBefore ? styles.inputGroupBefore : "",
		hasAddonAfter ? styles.inputGroupAfter : "",
		error ? styles.inputGroupError : "",
	]
		.filter(Boolean)
		.join(" ");

	const inputClass = [
		styles.input,
		styles[size],
		error ? styles.inputError : "",
		hasAddons ? styles.inputGrouped : "",
		isPassword ? styles.inputWithPasswordToggle : "",
	]
		.filter(Boolean)
		.join(" ");

	return (
		<div className={`${styles.field} ${colClass} ${className || ""}`} style={style}>
			{label ? (
				<label htmlFor={inputId} className={styles.label}>
					{label} {required ? <span className={styles.req}>*</span> : null}
				</label>
			) : null}

			<div className={hasAddons ? inputGroupClass : styles.inputWrapper}>
				{hasAddonBefore ? (
					<span className={styles.addon} aria-hidden="true">
						{addonBefore}
					</span>
				) : null}

				<div className={hasAddons ? styles.inputGroupedWrapper : styles.inputWrapper}>
					<input
						id={inputId}
						name={name}
						type={inputType}
						className={inputClass}
						value={value ?? ""}
						onChange={(e) => onChange?.(e.target.value)}
						placeholder={placeholder}
						required={required}
						disabled={disabled}
						autoFocus={autoFocus}
						autoComplete={autoComplete}
						aria-invalid={!!error || undefined}
						aria-describedby={describedBy}
					/>
					{isPassword && (
						<button
							type="button"
							className={styles.showPasswordBtn}
							onClick={() => setShowPassword((v) => !v)}
							aria-label={showPassword ? "Ocultar contraseña" : "Ver contraseña"}
							tabIndex={-1}
						>
							{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
						</button>
					)}
				</div>

				{hasAddonAfter ? (
					<span className={styles.addon} aria-hidden="true">
						{addonAfter}
					</span>
				) : null}
			</div>

			{error ? (
				<div id={`${inputId}-error`} className={styles.error}>
					{error}
				</div>
			) : helper ? (
				<div id={`${inputId}-helper`} className={styles.helper}>
					{helper}
				</div>
			) : null}
		</div>
	);
};

export default CustomInputText;
